import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es';
import deLocale from 'date-fns/locale/de';
import trLocale from 'date-fns/locale/tr';

import panelStyles from '../panelStyles';
import {getAppLang} from '../../../../utils';

const localeMap = {es: esLocale, de: deLocale, tr: trLocale};

// Takvimi kendi dugmemizden aciyoruz; picker'in kendi input'u gorunmesin.
// Modul seviyesinde duruyor ki her render'da yeni bilesen tipi uretilmesin.
const NoInput = () => null;

/**
 * Panelin donem secicisi: ok / ay / ok seklinde tek parca segment.
 * Ortadaki dugme yil+ay gorunumunde takvimi acar; ileri yon icinde
 * bulunulan aydan sonrasina kapalidir (gelecek ayin verisi yok).
 *
 * value ve onChange 'YYYY-MM' anahtariyla calisir: moment nesnesi mutable
 * oldugu icin state'te string tutmak surprizleri onluyor.
 */
export default function MonthPicker({value, onChange, minMonth, maxMonth}) {
    const {t} = useTranslation();
    const classes = panelStyles();
    const [open, setOpen] = useState(false);

    const selected = moment(value, 'YYYY-MM').startOf('month');
    const min = moment(minMonth, 'YYYY-MM').startOf('month');
    const max = moment(maxMonth, 'YYYY-MM').startOf('month');

    const isCurrent = selected.isSame(moment(), 'month');
    const canGoBack = selected.isAfter(min, 'month');
    const canGoForward = selected.isBefore(max, 'month');

    const shift = (months) => onChange(selected.clone().add(months, 'months').format('YYYY-MM'));

    // Chip'in sagindaki segmentler: ileri ok ve/veya "Bu ay"
    const hasRightSide = canGoForward || !isCurrent;

    return (
        <div className={classes.rangeNav}>
            <button
                type="button"
                className={classes.rangeArrow}
                onClick={() => shift(-1)}
                disabled={!canGoBack}
                aria-label={t('enw-panel-prev_month', 'Önceki ay')}
            >
                <i className="fas fa-chevron-left" />
            </button>

            <button
                type="button"
                className={`${classes.rangeChip}${hasRightSide ? '' : ` ${classes.rangeChipEnd}`}`}
                onClick={() => setOpen(true)}
            >
                <i className="fas fa-calendar-alt" />
                <b>{selected.format('MMMM YYYY')}</b>
                {/* Icinde bulunulan ay yarim: hangi araligin toplandigini yaz */}
                {isCurrent && ` · ${selected.format('D')}–${moment().format('D MMMM')}`}
                <i className="fas fa-chevron-down" />
            </button>

            {/* Ileri ok yalnizca gidilecek bir ay varken durur: icinde bulunulan
                ayda hep pasif kalacagi icin bosluk gibi gorunuyordu. */}
            {canGoForward && (
                <button
                    type="button"
                    className={classes.rangeArrow}
                    onClick={() => shift(1)}
                    aria-label={t('enw-panel-next_month', 'Sonraki ay')}
                >
                    <i className="fas fa-chevron-right" />
                </button>
            )}

            {/* Gecmis bir aydayken tek tikla canli doneme donus */}
            {!isCurrent && (
                <button
                    type="button"
                    className={classes.rangeToday}
                    onClick={() => onChange(moment().format('YYYY-MM'))}
                >
                    {t('enw-panel-this_month', 'Bu ay')}
                </button>
            )}

            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[getAppLang()]}>
                <DatePicker
                    autoOk
                    variant="dialog"
                    views={['year', 'month']}
                    openTo="month"
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    value={selected.toDate()}
                    onChange={(date) => {
                        onChange(moment(date).format('YYYY-MM'));
                        setOpen(false);
                    }}
                    minDate={min.toDate()}
                    maxDate={max.clone().endOf('month').toDate()}
                    cancelLabel={t('enw-panel-cancel', 'Vazgeç')}
                    okLabel={t('enw-panel-ok', 'Tamam')}
                    TextFieldComponent={NoInput}
                />
            </MuiPickersUtilsProvider>
        </div>
    );
}
